import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import ProductHeader from "./ProductHeader/ProductHeader.jsx";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg.jsx";
import isEmpty from "lodash/isEmpty";
import { FormattedMessage } from "react-intl";
import { ficheProduitPropTypes, photoPropTypes, videoPropTypes } from "app/utils/propTypes";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import HeadMetadata from "app/utils/metas/HeadMetadata";
import { OFFER_CONTRACT_TYPES, SLIDE_SHOW_PHOTO_TYPES } from "app/constants";
import NotFound from "app/pages/NotFound/NotFound";
import List from "app/pages/.shared/List";
import PropTypes from "prop-types";
import SocialBarPopover from "app/pages/.shared/social/SocialBarPopover";
import "./FicheProduit.scss";
import BadgeList from "app/pages/.shared/ColoredProductBadge/BadgeList";
import ProductActionButtonMobile from "app/pages/FicheProduit/components/ProductActionButtonMobile/ProductActionButtonMobile";
import get from "lodash/get";
import CookiePolicyFooterContainer from "app/pages/.shared/CookiePolicyFooter/CookiePolicyFooterContainer";
import IconPicture from "app/pages/.shared/static/icons/IconPicture";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import DealTimer from "app/pages/.shared/Deal/DealTimer/DealTimer";
import { sendTagOnOpenProductCarouselDesktop } from "app/utils/analytics.js";
import Collapse from "rc-collapse";
import LookAlikeProductsContainer from "app/pages/.shared/LookAlikeProduct/LookAlikeProductsContainer";
import OffersOverview from "app/pages/.shared/OffersOverview/OffersOverview";
import AvailableDurations from "app/pages/.shared/AvailableDurations";
import BookingBannerPlaceholderContainer from "app/pages/Booking/BookingBannerPlaceholder/BookingBannerPlaceholderContainer";
import NegociatedList from "app/pages/FicheProduit/NegociatedList/NegociatedList";
import IconShare from "app/pages/.shared/IconShare";
import useIntersectionObserver, {
	INTERSECTION_STATUS,
} from "app/pages/.shared/useIntersectionObserver";
import AsideOffersOverview from "app/pages/FicheProduit/AsideOffersOverview/AsideOffersOverview";
import { scrollToElement } from "app/utils/scroller";
import LazyLoad from "app/pages/.shared/LazyLoad/LazyLoad";
import loadable from "@loadable/component";
import IconGoToTopArrow from "app/pages/.shared/static/icons/IconGoToTopArrow";
import AppGlobalsContext from "app/AppGlobalsContext";
import CondensedBookWithConfidence from "app/pages/.shared/BookWithConfidence/CondensedBookWithConfidence";
import ProductStickyHeaderContainer from "app/pages/FicheProduit/ProductStickyHeader/ProductStickyHeaderContainer";
import AdditionalSections from "app/pages/FicheProduit/components/AdditionalSections/AdditionalSections";
import FullyCustomizableNote from "app/pages/.shared/FullyCustomizableNote/FullyCustomizableNote";
import PrintHeader from "./PrintHeader/PrintHeader";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";

const RentalSearchEngineContainer = loadable(() =>
	import(/* webpackChunkName: "product-page-rental-search-engine" */ "app/pages/PrePackage/rental/RentalSearchEngineContainer.jsx")
);

const SearchEngineContainer = loadable(() =>
	import(/* webpackChunkName: "product-page-search-engine" */ "app/pages/PrePackage/containers/SearchEngineContainer.jsx")
);

const Activities = loadable(() =>
	import(/* webpackChunkName: "product-page-activities" */ "./Activities/Activities.jsx")
);

const TransportPartnership = loadable(() =>
	import(/* webpackChunkName: "product-page-transport-partnership" */ "./TransportPartnership/TransportPartnership.jsx")
);

const Destination = loadable(() =>
	import(/* webpackChunkName: "product-page-destination" */ "./Destination/Destination.jsx")
);

const UsefulInformation = loadable(() =>
	import(/* webpackChunkName: "product-page-useful-information" */ "../components/UsefulInformation/UsefulInformation.jsx")
);

const ContactUs = loadable(() =>
	import(/* webpackChunkName: "product-page-contact-us" */ "app/pages/.shared/ContactUs/ContactUs")
);

const AccommodationSection = loadable(() =>
	import(/* webpackChunkName: "product-page-accommodation" */ "./Accommodation/AccommodationSection.jsx")
);

const RestaurantSection = loadable(() =>
	import(/* webpackChunkName: "product-page-restaurants" */ "./Restaurant/RestaurantSection.jsx")
);

const AccommodationTypes = loadable(() =>
	import(/* webpackChunkName: "product-page-rooms" */ "app/pages/FicheProduit/components/Accommodation/AccommodationTypes")
);

const AvailablePaymentTermsContainer = loadable(() =>
	import(/* webpackChunkName: "product-page-payment-terms" */ "app/pages/.shared/AvailablePaymentTerms/AvailablePaymentTermsContainer")
);

const AvisVerifiesContainer = loadable(() =>
	import(/* webpackChunkName: "product-page-payment-avis-verifies" */ "app/pages/.shared/AvisVerifies/AvisVerifiesContainer")
);

const PhotoCarousel = loadable(() =>
	import(/* webpackChunkName: "product-page-photo-caroussel" */ "app/pages/.shared/PhotoCarousel.jsx")
);

const SlideShow = loadable(() =>
	import(/* webpackChunkName: "product-page-slideshow" */ "app/pages/.shared/Slideshow/SlideShow.jsx")
);

const HERO_IMG_CLOUDINARY_OPTIONS = ["c_fill"];

const HERO_IMG_SIZES = {
	medium: { height: 460 },
	large: { height: 460 },
	xlarge: { height: 460 },
};

const HOTEL_PANEL_INDEX = "0";
const ROOMS_PANEL_INDEX = "1";
const RESTURANTS_PANEL_INDEX = "2";
const ACTIVITIES_PANEL_INDEX = "3";

const FicheProduit = ({
	resolution,
	ficheProduit = {},
	prePackageOffers = [],
	closeSlideshow,
	slideshow = {},
	negociated = [],
	allAvailableDurations,
	partnerCode,
	mobileSlideshowPhotos = [],
	desktopSlideShowPhotos = [],
	videos = [],
	shouldFetchLookAlikeProduts,
	specialOffer = {},
	openSlideshow,
	isWhiteLabel,
	googleAPIKey,
	showPfsMessage,
	updateBooking,
}) => {
	const [showStickyHeader, toggleStickyHeaderButton] = useState(false);
	const [showScrollTopButton, toggleScrollTopButton] = useState(false);
	const [showStickyOffersOverview, toggleOfferOverview] = useState(false);
	const showStickyRef = useRef();
	const searchEngineRef = useRef();
	const topRef = useRef();
	const intersectionStatus = useIntersectionObserver(showStickyRef.current);
	const topIntersectionStatus = useIntersectionObserver(topRef.current);

	const { shop } = useContext(AppGlobalsContext);

	// le mini search est affiché uniquement si l’utilisateur n’a pas commencé à renseigner ses préférences dans le search.
	const stickyClassname = classNames("product-page__sticky", {
		"product-page__sticky--top": showStickyOffersOverview,
	});

	const openPhotosSlideshow = useCallback(() => {
		sendTagOnOpenProductCarouselDesktop();
		openSlideshow(SLIDE_SHOW_PHOTO_TYPES.PHOTOS);
	}, []);

	useEffect(() => {
		toggleOfferOverview(intersectionStatus === INTERSECTION_STATUS.LEAVING_FROM_TOP);

		if (intersectionStatus === INTERSECTION_STATUS.LEAVING_FROM_TOP) {
			toggleScrollTopButton(true);
		} else if (intersectionStatus === INTERSECTION_STATUS.LEAVING_FROM_BOTTOM) {
			toggleScrollTopButton(false);
		}
	}, [intersectionStatus]);

	useEffect(() => {
		if (topIntersectionStatus === INTERSECTION_STATUS.ENTERING) {
			toggleStickyHeaderButton(false);
		} else if (topIntersectionStatus === INTERSECTION_STATUS.LEAVING_FROM_TOP) {
			toggleStickyHeaderButton(true);
		}
	}, [topIntersectionStatus]);

	const scrollToSearchEngine = useCallback(() => {
		scrollToElement({
			element: searchEngineRef.current,
			offset: 0,
			disableSmoothScroll: false,
		});
	}, []);

	const containsVideo = videos.length > 0;

	const isRental = ficheProduit.contractType === OFFER_CONTRACT_TYPES.RENTAL;

	const isDealExpired =
		get(ficheProduit, "expiration.hour") === 0 && get(ficheProduit, "expiration.day") === 0;

	const searchEngineNode = isRental ? <RentalSearchEngineContainer /> : <SearchEngineContainer />;

	const isScreenBelowMedium = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;

	const offerWithTransportLabel = prePackageOffers[0]?.pricing?.label;

	const scrollTopButtonClass = classNames({
		"scroll-top-button": true,
		"scroll-top-button--visible": showScrollTopButton,
	});

	const stickyHeaderClass = classNames({
		"product-page__sticky-header": true,
		"product-page__sticky-header--visible": showStickyHeader,
	});

	const handleScrollToTopClick = useCallback(() => {
		window.scrollTo({
			behavior: "smooth",
			left: 0,
			top: 0,
		});
	}, []);

	if (ficheProduit.notFound) {
		return <NotFound />;
	}

	const BLOCKS_TO_EXPAND_BY_DEFAULT = [
		HOTEL_PANEL_INDEX,
		ROOMS_PANEL_INDEX,
		RESTURANTS_PANEL_INDEX,
	];

	if (!ficheProduit.activity || !isEmpty(ficheProduit.activity)) {
		BLOCKS_TO_EXPAND_BY_DEFAULT.push(ACTIVITIES_PANEL_INDEX);
	}

	if (get(ficheProduit, "itinerary.blocks.length", 0) > 0) {
		BLOCKS_TO_EXPAND_BY_DEFAULT.push(`${Number(ACTIVITIES_PANEL_INDEX) + 1}`);
	}

	return (
		<div className="product-page">
			<PrintHeader showPfsMessage={showPfsMessage} />
			<HeadMetadata
				page="product"
				data={{
					name: ficheProduit.name,
					photo:
						desktopSlideShowPhotos && desktopSlideShowPhotos[0]
							? desktopSlideShowPhotos[0].url
							: "",
					saveUpTo: get(ficheProduit, "fromPriceType.value"),
					pricingType: get(ficheProduit, "fromPriceType.type"),
					pricingValue: get(ficheProduit, "fromPriceType.value"),
					id: ficheProduit.uri,
				}}
			/>

			{isWhiteLabel && (
				<div className={stickyHeaderClass}>
					<ProductStickyHeaderContainer />
				</div>
			)}

			{!isScreenBelowMedium && slideshow.isSlideshowOpened && (
				<SlideShow
					isOpened={slideshow.isSlideshowOpened}
					close={closeSlideshow}
					photos={desktopSlideShowPhotos}
				/>
			)}

			<div className="product-page__hero">
				{/* HERO MEDIA TYPE VIEWS ON MOBILE */}
				{isScreenBelowMedium && mobileSlideshowPhotos.length > 0 && (
					<PhotoCarousel photos={mobileSlideshowPhotos} resolution={resolution} />
				)}
				{/* HERO MEDIA TYPE VIEWS ON DESKTOP */}
				{!isScreenBelowMedium && desktopSlideShowPhotos && desktopSlideShowPhotos[0] && (
					<div className="hero-photo" onClick={openPhotosSlideshow}>
						<div className="hero__image">
							<BackgroundProgressiveImg
								sizes={HERO_IMG_SIZES}
								src={desktopSlideShowPhotos[0].url}
								thumbnail={desktopSlideShowPhotos[0].thumbnail}
								cloudinaryOptions={HERO_IMG_CLOUDINARY_OPTIONS}
								backgroundPosition={"center"}
							/>
						</div>
					</div>
				)}

				{isScreenBelowMedium ? (
					<>
						<div className="toolbar">
							<div className="toolbar__item">
								<SocialBarPopover
									createRef={true}
									popOverPosition={"left"}
									buttonLabel={<IconShare height={30} />}
									urlToShare={
										typeof window !== "undefined"
											? window.location.origin + window.location.pathname
											: ""
									}
								/>
							</div>
						</div>
						<div className="product-page__deal">
							<DealTimer
								hour={get(ficheProduit, "expiration.hour")}
								day={get(ficheProduit, "expiration.day")}
							/>
						</div>
					</>
				) : (
					<div className="product-page__toolbar">
						<div className="product-page__container">
							<div className="toolbar">
								<div className="toolbar__item" onClick={openPhotosSlideshow}>
									<IconPicture
										height={20}
										width={20}
										style={{ marginRight: "8px" }}
									/>

									<FormattedMessage id="product.page.photo.button.label" />
								</div>

								<div className="toolbar__item">
									<SocialBarPopover
										popOverPosition="above"
										buttonLabel={
											<div
												style={{
													display: "flex",
													alignItems: "center",
												}}
											>
												<IconShare
													height={25}
													width={20}
													style={{ marginRight: "6px" }}
												/>
												<FormattedMessage id="product.page.map.share.label" />
											</div>
										}
										urlToShare={
											typeof window !== "undefined"
												? window.location.origin + window.location.pathname
												: ""
										}
									/>
								</div>
							</div>
							<div className="product-page__deal">
								<DealTimer
									hour={get(ficheProduit, "expiration.hour")}
									day={get(ficheProduit, "expiration.day")}
								/>
							</div>
						</div>
					</div>
				)}
			</div>

			<div className="product-page__header">
				<div className="product-page__container">
					<div className="product-page__product-header" ref={topRef}>
						<ProductHeader
							name={ficheProduit.name}
							category={ficheProduit.category}
							categoryType={ficheProduit.categoryType}
							region={ficheProduit.region}
							resort={ficheProduit.resort}
							country={ficheProduit.country}
						/>

						<ProductRatingContainer
							tripAdvisor={ficheProduit?.tripAdvisor}
							holidayCheck={{
								...ficheProduit?.holidaycheck,
								size: "large",
							}}
						/>

						{ficheProduit.badges && ficheProduit.badges.length > 0 && (
							<div className="product-page__badges">
								<BadgeList badges={ficheProduit.badges} />
							</div>
						)}
					</div>

					{isScreenBelowMedium && (
						<div className="product-page__offers">
							<div className="product-page__offers-overview">
								<OffersOverview
									offers={prePackageOffers}
									updateBooking={updateBooking}
								/>
								<div className="product-page__available-durations">
									<AvailableDurations durations={allAvailableDurations} />
								</div>
							</div>
							<div className="product-page__negociated">
								<NegociatedList
									negociatedList={negociated}
									specialOffer={specialOffer}
									offerWithTransportLabel={offerWithTransportLabel}
								/>

								<FullyCustomizableNote />
							</div>
							<BookingBannerPlaceholderContainer disableFlashySpecialOffer />
						</div>
					)}

					<aside className="product-page__aside" ref={searchEngineRef}>
						<div className="product-page__container product-page__container--aside">
							{!isScreenBelowMedium && (
								<div className="product-page__search-engine">
									{searchEngineNode}
								</div>
							)}

							<div className="product-page__payment-terms">
								<LazyLoad topOffset={250} once>
									<AvailablePaymentTermsContainer />
								</LazyLoad>
							</div>

							{shop === "en-GB" && (
								<div className="product-page__atol-abta">
									<LazyLoad topOffset={250} once>
										<CondensedBookWithConfidence />
									</LazyLoad>
								</div>
							)}

							<div className="product-page__avis-verifies">
								<LazyLoad topOffset={250} once>
									<AvisVerifiesContainer />
								</LazyLoad>
							</div>

							{!isDealExpired && !isScreenBelowMedium && (
								<div className="product-page__contact" ref={showStickyRef}>
									<LazyLoad topOffset={250} once>
										<ContactUs productReference={ficheProduit.id} />
									</LazyLoad>
								</div>
							)}
						</div>
					</aside>
				</div>
			</div>

			<main className="product-page__main">
				<div className="product-page__container product-page__container--main">
					<div>
						{isScreenBelowMedium ? (
							<LazyLoad topOffset={250} once>
								<Collapse
									defaultActiveKey={BLOCKS_TO_EXPAND_BY_DEFAULT}
									expandIcon={({ isActive }) => {
										return (
											<i
												style={{
													transition: "transform .2s ease",
													transform: `rotate(${isActive ? 180 : 0}deg)`,
												}}
												className="icon icon--down-grey-dark"
											/>
										);
									}}
								>
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<h3 className="fiche-produit-item__title">
												{get(ficheProduit, "accommodation.intro.headline")}
											</h3>
										}
									>
										<div className="fiche-produit-item__body">
											<AccommodationSection
												videoUrl={containsVideo ? videos[0].url : undefined}
												videoType={
													containsVideo ? videos[0].videoType : undefined
												}
												tripAdvisor={get(ficheProduit, "tripAdvisor", {})}
												holidayCheck={get(ficheProduit, "holidaycheck", {})}
												name={ficheProduit.name}
												category={ficheProduit.category}
												categoryType={ficheProduit.categoryType}
												accommodation={ficheProduit.accommodation}
												sanitaryInformations={
													ficheProduit.sanitaryInformations
												}
												gpsLat={get(ficheProduit, "gps.lat")}
												gpsLong={get(ficheProduit, "gps.lon")}
												googleAPIKey={googleAPIKey}
											/>
										</div>
									</Collapse.Panel>

									{get(ficheProduit, "itinerary.blocks.length", 0) > 0 && (
										<Collapse.Panel
											headerClass="fiche-produit-item__header"
											header={
												<h3 className="fiche-produit-item__title">
													{get(ficheProduit, "itinerary.headline")}
												</h3>
											}
										>
											<div className="fiche-produit-item__body">
												<AdditionalSections
													blocks={get(ficheProduit, "itinerary.blocks")}
												/>
											</div>
										</Collapse.Panel>
									)}
									{get(ficheProduit, "accommodation.type.headline") && (
										<Collapse.Panel
											headerClass="fiche-produit-item__header"
											header={
												<h3 className="fiche-produit-item__title">
													{get(
														ficheProduit,
														"accommodation.type.headline"
													)}
												</h3>
											}
										>
											<div className="fiche-produit-item__body">
												<AccommodationTypes
													paragraphs={get(
														ficheProduit,
														"accommodation.type.paragraphs"
													)}
													photo={get(
														ficheProduit,
														"accommodation.type.photo"
													)}
													accommodationTypes={get(
														ficheProduit,
														"accommodation.type.accommodationTypes"
													)}
													facilities={get(
														ficheProduit,
														"accommodation.type.facilities"
													)}
												/>
											</div>
										</Collapse.Panel>
									)}
									{get(ficheProduit, "restaurant.headline") && (
										<Collapse.Panel
											headerClass="fiche-produit-item__header"
											header={
												<h3 className="fiche-produit-item__title">
													{get(ficheProduit, "restaurant.headline")}
												</h3>
											}
										>
											<div className="fiche-produit-item__body">
												<RestaurantSection
													intro={get(ficheProduit, "restaurant.intro")}
													restaurants={get(
														ficheProduit,
														"restaurant.restaurants"
													)}
													facilities={get(
														ficheProduit,
														"restaurant.facilities"
													)}
													boards={ficheProduit.boards}
												/>
											</div>
										</Collapse.Panel>
									)}
									{(!ficheProduit.activity ||
										!isEmpty(ficheProduit.activity)) && (
										<Collapse.Panel
											headerClass="fiche-produit-item__header"
											header={
												<h3 className="fiche-produit-item__title">
													{get(ficheProduit, "activity.label")}
												</h3>
											}
										>
											<div className="fiche-produit-item__body">
												<Activities
													intro={get(ficheProduit, "activity.intro")}
													facilities={get(
														ficheProduit,
														"activity.facilities"
													)}
													activities={get(
														ficheProduit,
														"activity.activities"
													)}
													description={get(
														ficheProduit,
														"activity.description"
													)}
												/>
											</div>
										</Collapse.Panel>
									)}

									{ficheProduit.additionalSections &&
										ficheProduit.additionalSections.map(additionalSection => {
											return (
												<Collapse.Panel
													key={additionalSection.headline}
													headerClass="fiche-produit-item__header"
													header={
														<h3 className="fiche-produit-item__title">
															{additionalSection.headline}
														</h3>
													}
												>
													<div className="fiche-produit-item__body">
														<AdditionalSections
															moreDetails={
																additionalSection.moreDetails
															}
															blocks={additionalSection.blocks}
														/>
													</div>
												</Collapse.Panel>
											);
										})}

									{!isEmpty(ficheProduit.destination) && (
										<Collapse.Panel
											headerClass="fiche-produit-item__header"
											header={
												<h3 className="fiche-produit-item__title">
													<FormattedMessage
														id="destination.discover"
														values={{
															destination: get(
																ficheProduit,
																"destination.label"
															),
														}}
													/>
												</h3>
											}
										>
											<div className="fiche-produit-item__body">
												<Destination
													paragraphs={get(
														ficheProduit,
														"destination.paragraphs"
													)}
													photo={get(ficheProduit, "destination.photo")}
													video={get(ficheProduit, "destination.video")}
													showVideoOnModal={!isScreenBelowMedium}
												/>
											</div>
										</Collapse.Panel>
									)}

									{get(ficheProduit, "transportPartnership.headline") && (
										<Collapse.Panel
											headerClass="fiche-produit-item__header"
											header={
												<h3 className="fiche-produit-item__title">
													{get(
														ficheProduit,
														"transportPartnership.headline"
													)}
												</h3>
											}
										>
											<div className="fiche-produit-item__body">
												<TransportPartnership
													intro={ficheProduit.transportPartnership.intro}
													logo={ficheProduit.transportPartnership.logo}
													paragraphs={
														ficheProduit.transportPartnership.paragraphs
													}
													photo={ficheProduit.transportPartnership.photo}
													video={ficheProduit.transportPartnership.video}
												/>
											</div>
										</Collapse.Panel>
									)}

									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<h3 className="fiche-produit-item__title">
												<FormattedMessage id="useful.information.title" />
											</h3>
										}
									>
										<div className="fiche-produit-item__body">
											<UsefulInformation
												partnerCode={partnerCode}
												usefulInformations={ficheProduit.usefulInformations}
											/>
										</div>
									</Collapse.Panel>
								</Collapse>
							</LazyLoad>
						) : (
							<>
								<div className="product-page__bloc product-page__bloc--highlighted">
									<NegociatedList
										negociatedList={negociated}
										specialOffer={specialOffer}
										offerWithTransportLabel={offerWithTransportLabel}
									/>
									<FullyCustomizableNote />
								</div>

								<div className="product-page__bloc">
									<LazyLoad topOffset={250} once>
										<AccommodationSection
											headline={get(
												ficheProduit,
												"accommodation.intro.headline"
											)}
											videoUrl={containsVideo ? videos[0].url : undefined}
											videoType={
												containsVideo ? videos[0].videoType : undefined
											}
											tripAdvisor={get(ficheProduit, "tripAdvisor", {})}
											holidayCheck={get(ficheProduit, "holidaycheck", {})}
											name={ficheProduit.name}
											category={ficheProduit.category}
											categoryType={ficheProduit.categoryType}
											accommodation={ficheProduit.accommodation}
											sanitaryInformations={ficheProduit.sanitaryInformations}
											gpsLat={get(ficheProduit, "gps.lat")}
											gpsLong={get(ficheProduit, "gps.lon")}
											googleAPIKey={googleAPIKey}
										/>
									</LazyLoad>
								</div>

								{get(ficheProduit, "itinerary.blocks.length", 0) > 0 && (
									<div className="product-page__bloc">
										<LazyLoad topOffset={250} once>
											<AdditionalSections
												headline={get(ficheProduit, "itinerary.headline")}
												moreDetails={get(
													ficheProduit,
													"itinerary.moreDetails"
												)}
												blocks={get(ficheProduit, "itinerary.blocks")}
											/>
										</LazyLoad>
									</div>
								)}
								{get(ficheProduit, "accommodation.type.headline") && (
									<div className="product-page__bloc">
										<LazyLoad topOffset={250} once>
											<AccommodationTypes
												headline={get(
													ficheProduit,
													"accommodation.type.headline"
												)}
												paragraphs={get(
													ficheProduit,
													"accommodation.type.paragraphs"
												)}
												photo={get(
													ficheProduit,
													"accommodation.type.photo"
												)}
												accommodationTypes={get(
													ficheProduit,
													"accommodation.type.accommodationTypes"
												)}
												facilities={get(
													ficheProduit,
													"accommodation.type.facilities"
												)}
											/>
										</LazyLoad>
									</div>
								)}

								{get(ficheProduit, "restaurant.headline") && (
									<div className="product-page__bloc">
										<LazyLoad topOffset={250} once>
											<RestaurantSection
												headline={get(ficheProduit, "restaurant.headline")}
												intro={get(ficheProduit, "restaurant.intro")}
												restaurants={get(
													ficheProduit,
													"restaurant.restaurants"
												)}
												facilities={get(
													ficheProduit,
													"restaurant.facilities"
												)}
												boards={ficheProduit.boards}
											/>
										</LazyLoad>
									</div>
								)}

								{(!ficheProduit.activity || !isEmpty(ficheProduit.activity)) && (
									<div className="product-page__bloc">
										<LazyLoad topOffset={250} once>
											<Activities
												label={get(ficheProduit, "activity.label")}
												intro={get(ficheProduit, "activity.intro")}
												facilities={get(
													ficheProduit,
													"activity.facilities"
												)}
												activities={get(
													ficheProduit,
													"activity.activities"
												)}
												description={get(
													ficheProduit,
													"activity.description"
												)}
											/>
										</LazyLoad>
									</div>
								)}

								{ficheProduit.additionalSections &&
									ficheProduit.additionalSections.map(additionalSection => {
										return (
											<div
												className="product-page__bloc"
												key={additionalSection.headline}
											>
												<LazyLoad topOffset={250} once>
													<AdditionalSections
														headline={additionalSection.headline}
														blocks={additionalSection.blocks}
													/>
												</LazyLoad>
											</div>
										);
									})}

								{!isEmpty(ficheProduit.destination) && (
									<div className="product-page__bloc">
										<LazyLoad topOffset={250} once>
											<Destination
												label={get(ficheProduit, "destination.label")}
												paragraphs={get(
													ficheProduit,
													"destination.paragraphs"
												)}
												photo={get(ficheProduit, "destination.photo")}
												video={get(ficheProduit, "destination.video")}
												showVideoOnModal={!isScreenBelowMedium}
											/>
										</LazyLoad>
									</div>
								)}

								{get(ficheProduit, "transportPartnership.headline") && (
									<div className="product-page__bloc">
										<LazyLoad topOffset={250} once>
											<TransportPartnership
												headline={
													ficheProduit.transportPartnership.headline
												}
												intro={ficheProduit.transportPartnership.intro}
												logo={ficheProduit.transportPartnership.logo}
												paragraphs={
													ficheProduit.transportPartnership.paragraphs
												}
												photo={ficheProduit.transportPartnership.photo}
												video={ficheProduit.transportPartnership.video}
											/>
										</LazyLoad>
									</div>
								)}
							</>
						)}
					</div>
					{!isScreenBelowMedium && !isDealExpired && (
						<div>
							<aside className={stickyClassname}>
								<div className="product-page__sticky-content">
									<AsideOffersOverview
										offers={prePackageOffers}
										hour={get(ficheProduit, "expiration.hour")}
										day={get(ficheProduit, "expiration.day")}
										onClick={scrollToSearchEngine}
									/>
									<div className="product-page__payment-terms">
										<AvailablePaymentTermsContainer />
									</div>

									{shop === "en-GB" && (
										<div className="product-page__atol-abta">
											<LazyLoad topOffset={250} once>
												<CondensedBookWithConfidence />
											</LazyLoad>
										</div>
									)}

									<div className="product-page__avis-verifies">
										<AvisVerifiesContainer />
									</div>

									<div className="product-page__contact">
										<ContactUs productReference={ficheProduit.id} />
									</div>
								</div>
							</aside>
						</div>
					)}
				</div>
			</main>

			<footer className="product-page__footer">
				{!isDealExpired && isScreenBelowMedium && (
					<div className="product-page__contact">
						<LazyLoad topOffset={250} once>
							<ContactUs productReference={ficheProduit.id} />
						</LazyLoad>
					</div>
				)}

				{shouldFetchLookAlikeProduts && (
					<div className="product-page__lookalike">
						<LazyLoad topOffset={250} once>
							<LookAlikeProductsContainer productId={ficheProduit.id} />
						</LazyLoad>
					</div>
				)}
			</footer>

			<div className="product-page__bottom-bar">
				{!isDealExpired && isScreenBelowMedium && <ProductActionButtonMobile />}
				<CookiePolicyFooterContainer />
			</div>

			<div className="product-page__included">
				{prePackageOffers.map(offer => {
					const offerPricingType = get(offer, "pricing.type");

					return (
						<div key={offerPricingType} className="product-page__included__item">
							<div className="included">
								<div className="included__header">
									{get(offer, "pricing.label")}
								</div>
								<div className="included__list">
									<div className="included__title">
										<FormattedMessage id="price.detail.included.title">
											{msg => <span>{`${msg} :`}</span>}
										</FormattedMessage>
									</div>
									<List
										items={get(ficheProduit, `included.${offerPricingType}`)}
										enableHTMLFormatting={true}
									/>
								</div>
								<div className="included__list">
									<div className="included__title">
										<FormattedMessage id="price.detail.not.included.title">
											{msg => <span>{`${msg} :`}</span>}
										</FormattedMessage>
									</div>
									<List
										items={get(ficheProduit, `notIncluded.${offerPricingType}`)}
										enableHTMLFormatting={true}
									/>
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{!isEmpty(ficheProduit.usefulInformations) && !isScreenBelowMedium && (
				<div id="useful-information" className="product-page__information">
					<LazyLoad topOffset={250} once>
						<UsefulInformation
							partnerCode={partnerCode}
							usefulInformations={ficheProduit.usefulInformations}
						/>
					</LazyLoad>
				</div>
			)}
			<PushNotificationContainer />

			<span className={scrollTopButtonClass} onClick={handleScrollToTopClick}>
				<IconGoToTopArrow width={50} height={50} />
			</span>
		</div>
	);
};

FicheProduit.propTypes = {
	ficheProduit: ficheProduitPropTypes,
	prePackageOffers: PropTypes.array,
	slideshow: PropTypes.shape({
		isSlideshowOpened: PropTypes.bool,
		photosType: PropTypes.string,
	}),
	negociated: PropTypes.array,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	videos: PropTypes.arrayOf(videoPropTypes),
	mobileSlideshowPhotos: PropTypes.arrayOf(photoPropTypes),
	desktopSlideShowPhotos: PropTypes.arrayOf(photoPropTypes),
	closeSlideshow: PropTypes.func,
	openSlideshow: PropTypes.func,
	resolution: PropTypes.string,
	partnerCode: PropTypes.string,
	shouldFetchLookAlikeProduts: PropTypes.bool,
	isWhiteLabel: PropTypes.bool,
	specialOffer: PropTypes.object,
	googleAPIKey: PropTypes.string,
	showPfsMessage: PropTypes.bool,
	updateBooking: PropTypes.func,
};

export default memo(FicheProduit);
